"use client";

import React, { FC, memo, useCallback, useState, Fragment } from "react";
import { cn, pubsub } from "utils";
import { useSnapshot } from "valtio";
import { Icon } from "@ospweb/sdk-ui";
import { ReportLogo } from "./ReportLogo";
import { ReportRatingChange } from "./ReportRatingChange";
import { Summary } from "./Summary";
import { ReportContent } from "./ReportContent";
// import { ShowOnPcOrMobile } from "@/src/components/ShowOnPcOrMobile";
import { ReportDate } from "./ReportDate";
import { ReportSymbol } from "./ReportSymbol";
import { ReportScoreBadge } from "./ReportScoreBadge";
import { openMobileDetailModal } from "./MobileDetailModal";
import Translations from "@translate/components/CoPilot/360Reports/ReportItem/ReportItem";
import { I18nUtil } from "@utils/i18n";
import { BaseFoldableItem } from "@/src/components/CoPilot/CopilotAgent/TimeLineItem/BasefoldableItem";
import { reportAccessStore } from "@/src/stores/reportAccessStore";
import { ChatStore, rootStore } from "@/src/store";
import type { AgentReport } from "@/src/features/api/memberApi";
import { LOCAL_STORAGE_KEYS } from "@/src/config";
import EdgenDrawerCallable from "@/src/components/EdgenDrawerCallable";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 报告数据接口
 * 基于 AgentReport，只添加必要的 UI 层转换字段
 */
export interface ReportData extends AgentReport {
  /** 资产符号（从 assets 提取） */
  symbol: string;
  /** 资产名称（从 assets 提取） */
  assetName: string;
  /** 资产图标 URL（从 assets 提取） */
  assetIcon?: string;
  /** 资产ID（从 assets 提取） */
  assetId: string;
  /** 评级变化方向：up 表示上升，down 表示下降 */
  ratingChange?: "up" | "down";
  /** 是否存在历史评级，控制变化区域展示 */
  hasPreviousScore: boolean;
  /** 资产简称 */
  short_name?: string;
}

interface ReportItemProps {
  /** 按钮文本 */
  buttonText?: string;
  /** 报告数据 */
  reportData: ReportData;
  /** 自定义类名 */
  className?: string;
  /** 是否显示评级变化 */
  showRatingChange?: boolean;
  /** 是否在弹窗中查看完整报告 */
  viewFullInModal?: boolean;
  isExpanded?: boolean;
  toggleExpand?: () => void;
  /** 默认情况移动端点击会跳转二级页面查看详情，可以通过此属性覆盖默认行为 */
  mobileClick?: (e: React.MouseEvent) => void;
}

/**
 * ReportItem 报告项组件
 * 显示360报告列表项，包含展开收起交互效果
 *
 * @param props ReportItemProps
 * @returns React.ReactElement
 */
export const ReportItem: FC<ReportItemProps> = memo(
  ({
    buttonText = I18n.t("Full Report"),
    reportData,
    className,
    showRatingChange = true,
    viewFullInModal,
    isExpanded,
    toggleExpand,
    mobileClick,
  }) => {
    const { isLoggedIn } = useSnapshot(rootStore.login);
    const content = reportData.content ?? {};
    const ratingChangeValue = reportData.ratingChange;
    const hasRatingHistory =
      reportData.hasPreviousScore && Boolean(ratingChangeValue);
    const showRatingSection = showRatingChange && hasRatingHistory;

    // 判断是否为受控模式：如果 isExpanded 为 undefined，则使用非受控模式
    const isControlled = isExpanded !== undefined;

    // 内部状态管理（仅在非受控模式下使用）
    const [internalExpanded, setInternalExpanded] = useState(false);

    // 根据模式选择展开状态
    const expandedState = isControlled ? isExpanded : internalExpanded;

    // 处理展开/收起切换
    const handleToggleExpand = useCallback(() => {
      if (isControlled) {
        // 受控模式：调用外部传入的 toggleExpand
        toggleExpand?.();
      } else {
        // 非受控模式：更新内部状态
        setInternalExpanded((prev) => !prev);
      }
    }, [isControlled, toggleExpand]);

    // 从 content 中获取评分信息
    const finalScore =
      content.level ??
      (content.final_score ? String(Math.round(content.final_score)) : "N/A");
    const previousScore = content.previous_level;
    /**
     * 渲染左侧评分区域
     */
    const renderLeftElement = ({ className }: { className?: string } = {}) => {
      return (
        <>
          {/* <ShowOnPcOrMobile showMode="pc"> */}
          <div
            className={cn(
              "flex flex-row-reverse justify-between md:justify-start md:gap-3 xl:flex-row xl:gap-6",
              className,
            )}
            data-tracking-scope="report-item-left-element"
          >
            <ReportScoreBadge rate={finalScore} className="flex-shrink-0" />
            {/* 报告标题和资产信息行 */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                {/* 报告logo */}
                <ReportLogo className="hidden md:flex" />
                <div
                  className={cn(
                    "flex items-center gap-x-2 max-md:grid max-md:grid-cols-[32px_1fr] max-md:grid-rows-[auto_auto]",
                    className,
                  )}
                >
                  {/* 资产信息 */}
                  <ReportSymbol
                    assetIcon={reportData.assetIcon || ""}
                    symbol={reportData.short_name || reportData.symbol}
                    className="max-md:contents"
                    imgClass="max-md:row-span-2"
                    textClass={cn({
                      "max-md:row-span-2": !(
                        showRatingSection && ratingChangeValue
                      ),
                    })}
                  />
                  {/* 评级变化 */}
                  {showRatingSection && ratingChangeValue && (
                    <ReportRatingChange
                      ratingChange={ratingChangeValue}
                      previousScore={previousScore}
                      finalScore={finalScore}
                    />
                  )}
                  {/* 日期 */}
                  <ReportDate
                    created={reportData.created}
                    className="hidden md:flex"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    };

    /**
     * 处理查看完整报告
     */
    const handleViewFullReport = useCallback(async () => {
      if (ChatStore.loading && viewFullInModal) {
        EdgenDrawerCallable.update({ isOpen: true });
        pubsub.emit(pubSubKeyMap.CHAT_DRAWER_STATE_CHANGE, true);
        return;
      }
      localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      reportAccessStore.requestReportAccess(
        {
          type: reportData.categories?.[0]?.toLocaleLowerCase() as
            | "crypto"
            | "stock",
          symbol: reportData.symbol,
          id: reportData.assetId,
          isModal: viewFullInModal,
        },
        isLoggedIn ?? false,
      );
    }, [
      isLoggedIn,
      reportData.assetId,
      reportData.categories,
      reportData.symbol,
      viewFullInModal,
    ]);

    const mobileViewmoreHandle = async () => {
      const modal = await openMobileDetailModal({
        className: "px-4 flex flex-col",
        headerOtherContent: (
          <div className="flex flex-1 justify-between">
            <ReportSymbol
              assetIcon={reportData.assetIcon || ""}
              symbol={reportData.short_name || reportData.symbol}
            />
            <ReportScoreBadge rate={finalScore} className="flex-shrink-0" />
          </div>
        ),
        children: (
          <Fragment>
            <div className="flex-1 overflow-y-auto no-scrollbar flex-col p-4">
              <Summary
                title={reportData.content?.title || ""}
                summary={reportData.content?.summary || ""}
              />
              <ReportContent
                content={content}
                data-tracking-scope="report-content"
              />
            </div>
            <div className="py-4">
              {renderFullReportButton({
                className: "md_button w-full button_m",
                onClick: () => {
                  modal.end(true);
                },
                children: (
                  <Fragment>
                    <Icon
                      name="credit"
                      size={20}
                      className="text-content_inverse_primary"
                    />
                    {buttonText}
                  </Fragment>
                ),
              })}
            </div>
          </Fragment>
        ),
      });
    };

    /**
     * 渲染完整报告按钮
     */
    const renderFullReportButton = useCallback(
      ({
        className,
        children,
        onClick,
      }: {
        className?: string;
        children?: React.ReactNode;
        onClick?: () => void;
      } = {}) => {
        return (
          <button
            aria-label="Report Item"
            data-openreplay-label={`Report Item Button | ${children || buttonText}`}
            id="btn-report-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleViewFullReport();
              onClick?.();
            }}
            className={cn(
              "button primary_button sm_button button_s text-content_inverse_primary",
              className,
            )}
            data-tracking-scope="full-report-button"
          >
            {children || buttonText}
          </button>
        );
      },
      [handleViewFullReport, buttonText],
    );

    /**
     * 渲染展开后的内容
     */
    const renderExpandedContent = ({
      className,
    }: { className?: string } = {}) => {
      return (
        <div
          className={cn("flex flex-col gap-3", className)}
          data-tracking-scope="report-item-expanded-content"
        >
          <Summary
            title={reportData.content?.title || ""}
            summary={reportData.content?.summary || ""}
          />
          {expandedState && (
            <ReportContent
              content={content}
              data-tracking-scope="report-content"
            />
          )}
        </div>
      );
    };

    return (
      <div
        data-openreplay-label="ReportItem · ReportItem · div"
        className={cn(
          "w-full flex flex-col gap-3 md:block md:gap-0",
          className,
        )}
        onClick={mobileClick || mobileViewmoreHandle}
        data-tracking-scope="report-item"
      >
        {renderLeftElement({ className: "md:hidden" })}
        {renderExpandedContent({ className: "md:hidden" })}
        <BaseFoldableItem
          isOpen={expandedState}
          onOpenChange={handleToggleExpand}
          leftElement={renderLeftElement()}
          AskQuestionButton={renderFullReportButton()}
          className="hidden md:block w-full p-4"
          containerClassName="gap-0 md:gap-0 xl:gap-4"
          titleContainerClassName="items-start border-b-0 xl:border-b-1 border-border_solid pb-2 md:pb-2 xl:pb-3"
        >
          {renderExpandedContent()}
        </BaseFoldableItem>
      </div>
    );
  },
);

ReportItem.displayName = "ReportItem";
